import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { normalizeExportText } from "../lib/exportText";
import { useRegisterViewRefresh } from "../lib/viewRefresh";

function csvCell(value: unknown): string {
  if (value == null) return "";
  const raw = normalizeExportText(value);
  if (/[",\n\r]/.test(raw)) {
    return `"${raw.replace(/"/g, "\"\"")}"`;
  }
  return raw;
}

function reasonLabel(reason: string): string {
  if (reason === "missing") return "Missing";
  if (reason === "low_quantity") return "Low Quantity";
  if (reason === "expired") return "Expired";
  if (reason === "expiring_90d") return "Expiring <=90d";
  return reason;
}

function reasonChipClass(reason: string): string {
  return reason === "expiring_90d" ? "reason-chip-yellow" : "reason-chip-red";
}

export function OrderingScreen() {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = async (forceRefresh = false) => {
    setLoading(true);
    setError("");
    try {
      const result = await api.getSimpleOrderList(forceRefresh ? { forceRefresh: true } : undefined);
      setData(result);
    } catch (err: any) {
      setError(err?.message ?? "Failed to load ordering");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useRegisterViewRefresh(() => refresh(true), {
    disabled: loading,
    disabledReason: "Ordering is already loading."
  });

  const orderingRows = useMemo(() => {
    if (!data?.items) return [];
    const q = filter.trim().toLowerCase();

    return data.items
      .map((row: any) => ({
        catalogItemId: row.catalogItemId,
        itemName: row.itemName,
        category: row.category,
        subcategory: row.subcategory,
        isComplianceRequired: Boolean(row.isComplianceRequired),
        required: Number(row.required ?? 0),
        onApparatus: Number(row.onApparatus ?? 0),
        usableOnApparatus: Number(row.usableOnApparatus ?? 0),
        par: Number(row.par ?? 0),
        usableStock: Number(row.stockQty ?? 0),
        qtyToOrder: Math.max(0, Number(row.qtyToOrder ?? 0)),
        expiring90StockQty: Number(row.expiring90 ?? 0),
        expiredStockQty: Number(row.expired ?? 0),
        backorderQty: Number(row.backorderQty ?? 0),
        hasBackorder: Boolean(row.hasBackorder),
        apparatusAlerts: Array.isArray(row.apparatusAlerts)
          ? row.apparatusAlerts
              .map((alert: any) => ({
                apparatusId: String(alert?.apparatusId ?? ""),
                apparatusName: String(alert?.apparatusName ?? ""),
                bagId: String(alert?.bagId ?? ""),
                bagName: String(alert?.bagName ?? ""),
                reason: String(alert?.reason ?? ""),
                quantity: Number(alert?.quantity ?? 0)
              }))
              .filter((alert: any) => alert.apparatusName && alert.bagName && alert.reason && alert.quantity > 0)
          : []
      }))
      .filter((row: any) => row.par > 0)
      .map((row: any) => ({
        ...row,
        apparatusExpiredQty: row.apparatusAlerts
          .filter((alert: any) => alert.reason === "expired")
          .reduce((sum: number, alert: any) => sum + alert.quantity, 0),
        apparatusExpiring90Qty: row.apparatusAlerts
          .filter((alert: any) => alert.reason === "expiring_90d")
          .reduce((sum: number, alert: any) => sum + alert.quantity, 0),
        apparatusMissingLowQty: row.apparatusAlerts
          .filter((alert: any) => alert.reason === "missing" || alert.reason === "low_quantity")
          .reduce((sum: number, alert: any) => sum + alert.quantity, 0),
        apparatusOutOfStock: row.apparatusAlerts.some((alert: any) => alert.reason === "missing"),
        hasApparatusIssue: row.apparatusAlerts.length > 0,
        hasUsableStock: row.usableStock > 0
      }))
      .map((row: any) => {
        const apparatusShortageQty = Math.max(0, row.required - row.usableOnApparatus, row.apparatusMissingLowQty);
        const apparatusReplacementQty = apparatusShortageQty + row.apparatusExpiring90Qty;
        const stockBelowPar = row.par > 0 && row.qtyToOrder > 0;
        const apparatusIssueNoStock = row.hasApparatusIssue && !row.hasUsableStock;
        const mustOrder = stockBelowPar || apparatusIssueNoStock;
        const canReplaceFromStock = row.hasApparatusIssue && row.hasUsableStock;
        const mustOrderReasons = [
          stockBelowPar ? "Stock below PAR" : "",
          apparatusIssueNoStock ? "Apparatus issue with no usable stock" : ""
        ].filter(Boolean);

        return {
          ...row,
          apparatusShortageQty,
          apparatusReplacementQty,
          stockBelowPar,
          mustOrder,
          canReplaceFromStock,
          mustOrderReasons,
          suggestedOrderQty: Math.max(row.qtyToOrder, apparatusIssueNoStock ? Math.max(0, apparatusReplacementQty - row.usableStock) : 0)
        };
      })
      .filter((row: any) => row.mustOrder || row.canReplaceFromStock)
      .filter((row: any) => {
        if (!q) return true;
        const apparatusText = row.apparatusAlerts
          .map((alert: any) => `${alert.apparatusName} ${alert.bagName} ${reasonLabel(alert.reason)}`)
          .join(" ");
        const actionText = `${row.mustOrder ? "must order" : ""} ${row.canReplaceFromStock ? "replace from stock" : ""}`;
        const text = `${row.itemName} ${row.category ?? ""} ${row.subcategory ?? ""} ${apparatusText} ${actionText}`.toLowerCase();
        return text.includes(q);
      });
  }, [data, filter]);

  const mustOrderRows = useMemo(() => orderingRows.filter((row: any) => row.mustOrder), [orderingRows]);
  const replaceFromStockRows = useMemo(
    () => orderingRows.filter((row: any) => row.canReplaceFromStock),
    [orderingRows]
  );

  const exportPlanningCsv = () => {
    const header = [
      "Bucket",
      "Item",
      "Category",
      "Subcategory",
      "Required On Apparatus",
      "On Apparatus",
      "Usable On Apparatus",
      "Apparatus Out Of Stock",
      "PAR",
      "Usable Stock",
      "PAR Gap",
      "Expired Stock Qty",
      "Expiring <=90 Days Stock Qty",
      "Apparatus Expired Qty",
      "Apparatus Expiring <=90 Days Qty",
      "Backorder Qty",
      "Apparatus Sources",
      "Qty To Order",
      "Suggested Order Qty",
      "Action"
    ];

    const lines = [header.map(csvCell).join(",")];
    for (const row of orderingRows) {
      lines.push(
        [
          [
            row.mustOrder ? "Must Order" : "",
            row.canReplaceFromStock ? "Replace From Stock" : ""
          ]
            .filter(Boolean)
            .join("; "),
          row.itemName,
          row.category ?? "",
          row.subcategory ?? "",
          row.required,
          row.onApparatus,
          row.usableOnApparatus,
          row.apparatusOutOfStock ? "Yes" : "No",
          row.par,
          row.usableStock,
          row.qtyToOrder,
          row.expiredStockQty,
          row.expiring90StockQty,
          row.apparatusExpiredQty,
          row.apparatusExpiring90Qty,
          row.backorderQty ?? 0,
          row.apparatusAlerts
            .map(
              (alert: any) => `${alert.apparatusName} / ${alert.bagName} / ${reasonLabel(alert.reason)} (${alert.quantity})`
            )
            .join("; "),
          row.qtyToOrder,
          row.suggestedOrderQty,
          [
            row.mustOrder ? `Order Now${row.mustOrderReasons.length ? `: ${row.mustOrderReasons.join("; ")}` : ""}` : "",
            row.canReplaceFromStock ? "Use Stock to Replace" : ""
          ]
            .filter(Boolean)
            .join("; ")
        ]
          .map(csvCell)
          .join(",")
      );
    }

    const blob = new Blob([`\uFEFF${lines.join("\n")}\n`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stock-par-planning-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (error && !data) return <p className="error-text">{error}</p>;
  if (!data) return <p className="muted">Loading ordering...</p>;

  return (
    <div className="stack-md">
      {error ? <p className="error-text">{error}</p> : null}
      <section className="card stack-sm">
        <div className="row-between">
          <h2>Ordering</h2>
          <input
            className="input inline-control"
            placeholder="Filter items"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </div>
        <p className="muted small" role="status">
          {loading
            ? "Updating…"
            : data.generatedAt
              ? `Last updated ${new Date(data.generatedAt).toLocaleString()}`
              : null}
        </p>
      </section>

      <section className="card stack-sm">
        <div className="row-between">
          <h2>Ordering Report</h2>
          <button className="btn btn-secondary" onClick={exportPlanningCsv} disabled={orderingRows.length === 0}>
            Export CSV
          </button>
        </div>
        <p>
          Lines: <strong>{orderingRows.length}</strong> · Total Qty To Order:{" "}
          <strong>{mustOrderRows.reduce((sum: number, row: any) => sum + row.suggestedOrderQty, 0)}</strong>
        </p>
        <div className="stack-sm">
          <h3>Must Order Now (No Usable Stock / Below PAR / Apparatus Gap)</h3>
          <div className="list">
            {mustOrderRows.map((row: any) => (
              <div key={row.catalogItemId} className="list-item static">
                <div className="stack-xs">
                  <strong className="text-wrap-anywhere">{row.itemName}</strong>
                  <p className="muted small">
                    Required {row.required} · On Apparatus {row.usableOnApparatus} · Usable Stock {row.usableStock}
                    {row.expiredStockQty > 0 ? ` · Expired ${row.expiredStockQty}` : ""}
                    {row.expiring90StockQty > 0 ? ` · Expiring<=90 ${row.expiring90StockQty}` : ""}
                    {row.hasBackorder ? ` · Backorder ${row.backorderQty}` : ""}
                  </p>
                  <div className="tag-row">
                    {row.mustOrderReasons.map((reason: string) => (
                      <span key={`${row.catalogItemId}-${reason}`} className="reason-chip reason-chip-red">
                        {reason}
                      </span>
                    ))}
                    {row.apparatusAlerts.map((alert: any, index: number) => (
                      <span
                        key={`${row.catalogItemId}-${alert.apparatusId}-${alert.bagId}-${alert.reason}-${index}`}
                        className={`reason-chip ${reasonChipClass(alert.reason)}`}
                      >
                        {alert.apparatusName} · {alert.bagName} · {reasonLabel(alert.reason)} ({alert.quantity})
                      </span>
                    ))}
                  </div>
                </div>
                <strong className="red">Order {row.suggestedOrderQty}</strong>
              </div>
            ))}
            {mustOrderRows.length === 0 ? <p className="small muted">No items currently require immediate ordering.</p> : null}
          </div>
        </div>
        <div className="stack-sm">
          <h3>Replace From Stock (Out/Expiring On Apparatus + Stock Available)</h3>
          <div className="list">
            {replaceFromStockRows.map((row: any) => (
              <div key={row.catalogItemId} className="list-item static">
                <div className="stack-xs">
                  <strong className="text-wrap-anywhere">{row.itemName}</strong>
                  <p className="muted small">
                    Required {row.required} · On Apparatus {row.usableOnApparatus} · Usable Stock {row.usableStock}
                    {row.expiredStockQty > 0 ? ` · Expired ${row.expiredStockQty}` : ""}
                    {row.expiring90StockQty > 0 ? ` · Expiring<=90 ${row.expiring90StockQty}` : ""}
                    {row.hasBackorder ? ` · Backorder ${row.backorderQty}` : ""}
                  </p>
                  <div className="tag-row">
                    {row.apparatusAlerts.map((alert: any, index: number) => (
                      <span
                        key={`${row.catalogItemId}-${alert.apparatusId}-${alert.bagId}-${alert.reason}-${index}`}
                        className={`reason-chip ${reasonChipClass(alert.reason)}`}
                      >
                        {alert.apparatusName} · {alert.bagName} · {reasonLabel(alert.reason)} ({alert.quantity})
                      </span>
                    ))}
                  </div>
                </div>
                <strong className="ok">Use Stock</strong>
              </div>
            ))}
            {replaceFromStockRows.length === 0 ? <p className="small muted">No apparatus issues are currently covered by stock.</p> : null}
          </div>
        </div>
      </section>
    </div>
  );
}
